/**
 * The Sheng Ji game view. Composes:
 *   - CircleTable with nameplates and seat-local play zones
 *   - Hand (the local player's cards)
 *   - Phase-specific action panels (declare, bury kitty, play)
 *   - TrumpPanel and ScoreStrip
 *   - Event log (bottom-left)
 *
 * Only the local player's hand is private. Everything else is in the public
 * state blob and trivially renderable.
 */

import { useMemo, useState } from "react";
import type {
  CardId,
  RankShort,
  ShengJiPublicState,
  SuitId,
} from "../../../../shared/games/shengji.js";
import type { ClientMsg, GameEvent } from "../../../../shared/protocol.js";
import { CircleTable } from "../../components/CircleTable.js";
import { Hand } from "../../components/Hand.js";
import { PlayingCard, type CardDecorator } from "../../components/PlayingCard.js";
import { Select } from "../../components/Select.js";
import {
  lerpCardWidth,
  useDynamicTableTop,
  useResetOnChange,
  useWindowWidth,
} from "../../lib/uiHooks.js";
import { ScoreBar } from "./ScoreBar.js";
import { TrumpPanel } from "./TrumpPanel.js";
import {
  computeLegalCardSet,
  isDeclarablePair,
  isDeclarableSingle,
} from "./legality.js";

interface ShengJiGameProps {
  state: ShengJiPublicState;
  yourHand: CardId[];
  yourSeat: number | null;
  /** Declarer's full friend-call list in FtF mode (private). null otherwise. */
  yourFriendCalls:
    | {
        rank: RankShort;
        suit: SuitId;
        ordinal: number;
        public: boolean;
        revealedSeat: number | null;
      }[]
    | null;
  events: GameEvent[];
  send: (m: ClientMsg) => void;
  /** UA-detected mobile device. Switches the hand to a scrollable layout. */
  mobile?: boolean;
}

export function ShengJiGame({ state, yourHand, yourSeat, yourFriendCalls, events, send, mobile = false }: ShengJiGameProps) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showLastTrick, setShowLastTrick] = useState(false);

  // Reset selection when phase or trick changes.
  const phaseKey = `${state.phase}:${state.currentTrickPlays.length}:${state.completedTricks.length}`;
  useResetOnChange(phaseKey, () => setSelected(new Set()));

  const legal = useMemo(() => {
    if (yourSeat === null) return new Set<CardId>();
    return computeLegalCardSet(yourHand, state, yourSeat);
  }, [yourHand, state, yourSeat]);

  const selectedCardIds = useMemo<CardId[]>(
    () => [...selected].map((i) => yourHand[i]!).filter(Boolean),
    [selected, yourHand]
  );

  function toggle(i: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  const isMyTurnToPlay = state.phase === "play" && state.currentSeat === yourSeat;
  // Kitty pickup + bury belongs to the dealer, not the declarer.
  const isMyTurnToBury = state.phase === "kitty" && state.dealer === yourSeat;
  const isMyTurnToCallFriends =
    state.phase === "call_friends" && state.declarer === yourSeat;
  const canDeclareNow = state.phase === "dealing" && yourSeat !== null;

  // Cards on the table scale with the viewport so they're readable on big
  // monitors and don't crowd seats on small windows. Mobile is sized for
  // the landscape-rotated layout the phone CSS forces — see
  // `body.mobile-device.in-game-view` rotation rule in global.css.
  const windowWidth = useWindowWidth();
  const playZoneCardWidth = mobile ? 72 : lerpCardWidth(windowWidth, 80, 120);
  const declCardWidth = mobile ? 60 : lerpCardWidth(windowWidth, 66, 96);
  const tableTopRef = useDynamicTableTop();

  return (
    <div className="game-view" ref={tableTopRef}>
      <CircleTable
        seatCount={state.players.length}
        mySeatIndex={yourSeat}
        renderSeat={(i) => (
          <SeatView
            seat={i}
            state={state}
            yourSeat={yourSeat}
            mobile={mobile}
            playZoneCardWidth={playZoneCardWidth}
            declCardWidth={declCardWidth}
          />
        )}
      >
        <CenterPile state={state} />
      </CircleTable>

      <ScoreBar state={state} yourSeat={yourSeat} />
      <TrumpPanel state={state} />
      <BottomLeftInfo state={state} yourSeat={yourSeat} />

      {yourSeat !== null && (
        <Hand
          cards={yourHand}
          legal={state.phase === "play" ? legal : undefined}
          selected={selected}
          onToggle={toggle}
          trumpRank={state.trumpRank}
          trumpSuit={state.trumpSuit}
          decoratorOf={shengJiDecorator}
          mobile={mobile}
        />
      )}

      <ActionBar
        state={state}
        yourSeat={yourSeat}
        selectedCards={selectedCardIds}
        canDeclareNow={canDeclareNow}
        isMyTurnToPlay={isMyTurnToPlay}
        isMyTurnToBury={isMyTurnToBury}
        isMyTurnToCallFriends={isMyTurnToCallFriends}
        send={send}
        onAfterSubmit={() => setSelected(new Set())}
      />

      <FriendCallsPanel
        state={state}
        yourSeat={yourSeat}
        yourFriendCalls={yourFriendCalls}
      />

      {state.completedTricks.length > 0 && state.phase === "play" && (
        <button
          className="last-trick-button"
          onClick={() => setShowLastTrick((v) => !v)}
          title="View the previous trick"
        >
          {showLastTrick ? "Close" : "Last trick"}
        </button>
      )}
      {showLastTrick && state.completedTricks.length > 0 && (
        <LastTrickPanel
          trick={state.completedTricks[state.completedTricks.length - 1]!}
          players={state.players}
          onClose={() => setShowLastTrick(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Last-trick viewer
// ---------------------------------------------------------------------------

function LastTrickPanel({
  trick,
  players,
  onClose,
}: {
  trick: ShengJiPublicState["completedTricks"][number];
  players: ShengJiPublicState["players"];
  onClose: () => void;
}) {
  // Cards shrink on phone so a row of 4-6 fits within the panel's
  // viewport-bound width. The label + cards layout also stacks
  // vertically on small screens — see `.last-trick-play` CSS.
  const windowWidth = useWindowWidth();
  const cardW = windowWidth < 700 ? 34 : 56;
  return (
    <div className="last-trick-panel">
      <div className="row between" style={{ marginBottom: 10 }}>
        <strong>Last trick</strong>
        <button onClick={onClose} style={{ padding: "4px 10px" }}>Close</button>
      </div>
      <div className="last-trick-plays">
        {trick.plays.map((p, i) => {
          const isWinner = p.seat === trick.winner;
          const leader = trick.leader === p.seat;
          return (
            <div key={i} className={`last-trick-play ${isWinner ? "winner" : ""}`}>
              <div className="last-trick-label">
                {players[p.seat]?.displayName ?? `Seat ${p.seat}`}
                {leader && <span className="pill" style={{ marginLeft: 4 }}>LEAD</span>}
                {isWinner && <span className="role-tag mine" style={{ marginLeft: 4 }}>WON</span>}
              </div>
              <div className="last-trick-cards">
                {p.cards.map((cid, j) => (
                  <PlayingCard key={`${cid}-${j}`} card={cid} width={cardW} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
        Points in trick: <strong style={{ color: "var(--accent)" }}>{trick.points}</strong>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

import { useEffect } from "react";

/**
 * Sheng Ji per-card decoration: point cards (5/10/K) get a gold stripe
 * down the left edge so players can see at a glance which cards are
 * worth points to capture.
 */
export function shengJiDecorator(cardId: CardId): CardDecorator {
  if (cardId === "SJ" || cardId === "BJ") return null;
  const rank = cardId[0];
  if (rank === "5" || rank === "T" || rank === "K") {
    return <rect x={4} y={40} width={5} height={60} rx={2.5} fill="#d4af37" />;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Seat view (nameplate + play zone)
// ---------------------------------------------------------------------------

function SeatView({
  seat,
  state,
  yourSeat,
  mobile,
  playZoneCardWidth,
  declCardWidth,
}: {
  seat: number;
  state: ShengJiPublicState;
  yourSeat: number | null;
  mobile: boolean;
  playZoneCardWidth: number;
  declCardWidth: number;
}) {
  const p = state.players[seat]!;
  const isDealer = state.dealer === seat;
  const isDeclarer = state.declarer === seat;
  const isTurn =
    (state.phase === "play" && state.currentSeat === seat) ||
    (state.phase === "kitty" && state.dealer === seat) ||
    (state.phase === "call_friends" && state.declarer === seat);
  const myTeam = yourSeat !== null ? (state.players[yourSeat]?.team ?? null) : null;
  const isMe = seat === yourSeat;
  // A teammate is "known" only when both my team and theirs are revealed and equal.
  const isPartner = !isMe && myTeam !== null && p.team !== null && p.team === myTeam;
  const isOpponent = !isMe && myTeam !== null && p.team !== null && p.team !== myTeam;
  const isUnrevealed = p.team === null;

  let relationClass = "";
  if (isMe) relationClass = "mine";
  else if (isPartner) relationClass = "partner";
  else if (isOpponent) relationClass = "opponent";
  else if (isUnrevealed) relationClass = "unknown-team";
  else relationClass = p.team === 0 ? "team-0" : "team-1";

  const trickPlay = state.currentTrickPlays.find((tp) => tp.seat === seat);
  const isWinning =
    state.currentTrickPlays.length > 0 &&
    state.currentTrickPlays[state.currentTrickWinningIndex]?.seat === seat;

  const declCards =
    state.phase === "dealing" && state.declaration?.player === seat
      ? state.declaration.cards
      : null;

  // Per-seat rank (each player tracks their own progress). Falls back to the
  // team-rank tuple for legacy fixed-team display.
  const seatRank = p.rank;

  return (
    <>
      <div
        className={[
          "nameplate",
          relationClass,
          isTurn ? "turn" : "",
          isDealer ? "dealer" : "",
          isDeclarer ? "declarer" : "",
        ].filter(Boolean).join(" ")}
      >
        <div className="nameplate-row">
          {isMe && <span className="role-tag mine">YOU</span>}
          {isPartner && <span className="role-tag partner">PARTNER</span>}
          {isDeclarer && <span className="declarer-mark" title="Declarer">★</span>}
          <span className={`conn-dot ${p.connected ? "" : "off"}`} />
          <span className="player-name">{p.displayName}</span>
          {isDealer && <span className="dealer-mark" title="Dealer">D</span>}
        </div>
        <div className="nameplate-row sub">
          <span className="rank-line">rank {seatRank}</span>
          {isTurn && state.phase === "play" && (
            <SeatTurnCountdown turnStartedAt={state.turnStartedAt} />
          )}
        </div>
      </div>
      <div className={`play-zone ${isWinning ? "winning" : ""}`}>
        {isWinning && <div className="winning-badge">WINNING</div>}
        {trickPlay
          ? <PlayZoneCards cards={trickPlay.cards} width={playZoneCardWidth} decoratorOf={shengJiDecorator} />
          : declCards
          ? <PlayZoneCards cards={declCards} width={declCardWidth} keyPrefix="decl" decoratorOf={shengJiDecorator} />
          : null}
      </div>
    </>
  );
}

/**
 * Renders a row of played cards with negative right-margin so multi-card
 * combos (pairs, tractors, throws) overlap and fit within the seat's
 * play-zone instead of overflowing into adjacent UI.
 *
 * Each card lives in its own wrapper so we can assign an ascending
 * z-index — without it, CSS transforms on the card (selected lift etc.)
 * would establish stacking contexts that flip the painting order.
 */
export function PlayZoneCards({
  cards,
  width,
  keyPrefix = "play",
  decoratorOf,
}: {
  cards: readonly CardId[];
  width: number;
  keyPrefix?: string;
  decoratorOf?: (id: CardId) => import("../../components/PlayingCard.js").CardDecorator;
}) {
  // Each card shows ~65% of its width before the next one starts.
  const overlap = cards.length > 1 ? -Math.round(width * 0.35) : 0;
  return (
    <>
      {cards.map((cid, idx) => (
        <div
          key={`${keyPrefix}-${cid}-${idx}`}
          className="play-card-wrapper"
          style={{
            marginRight: idx === cards.length - 1 ? 0 : overlap,
            position: "relative",
            zIndex: idx + 1,
          }}
        >
          <PlayingCard card={cid} width={width} decorator={decoratorOf?.(cid)} />
        </div>
      ))}
    </>
  );
}

/**
 * Small "30s" countdown that appears under the active seat's name during
 * the play phase. Mirrors the server-side 60s timeout — if it runs out the
 * server auto-plays a fallback action.
 */
function SeatTurnCountdown({ turnStartedAt }: { turnStartedAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);
  const remaining = Math.max(0, 60_000 - (now - turnStartedAt));
  const seconds = Math.ceil(remaining / 1000);
  const urgent = remaining <= 10_000;
  return (
    <span className={`turn-countdown ${urgent ? "urgent" : ""}`}>{seconds}s</span>
  );
}

// ---------------------------------------------------------------------------
// Center pile (kitty placeholder, undealt deck count, status)
// ---------------------------------------------------------------------------

function CenterPile({ state }: { state: ShengJiPublicState }) {
  if (state.phase === "dealing") {
    // Once the last card is dealt the engine holds the bid open for a
    // short window so a late declaration can still be overridden. Show
    // a live countdown during that window.
    if (state.bidLockUntil !== null) {
      return <BidLockCountdown until={state.bidLockUntil} />;
    }
    return (
      <div className="center-pile">
        <span className="label">Dealing…</span>
        <span className="points">{state.deckRemaining} cards left</span>
      </div>
    );
  }
  if (state.phase === "kitty") {
    return (
      <div className="center-pile">
        <span className="label">Dealer is choosing the kitty…</span>
      </div>
    );
  }
  if (state.phase === "call_friends") {
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 14 }}>
        <span className="label">
          Declarer is calling {state.friendsToCall === 1 ? "a friend" : `${state.friendsToCall} friends`}…
        </span>
        <span className="points" style={{ fontSize: 14, marginTop: 4 }}>
          Teams stay hidden until the called cards are played
        </span>
      </div>
    );
  }
  if (state.phase === "round_end") {
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 18 }}>
        <span className="label">Round complete</span>
        <span className="points">Attackers: {state.attackersPoints}</span>
        {state.kittyRevealed && (
          <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
            <span className="label" style={{ alignSelf: "center" }}>Kitty:</span>
            {state.kittyRevealed.map((c, i) => <PlayingCard key={i} card={c} width={62} />)}
          </div>
        )}
      </div>
    );
  }
  if (state.phase === "match_end") {
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 18 }}>
        <span className="label" style={{ fontSize: 18 }}>Match complete</span>
        <span className="points">Final ranks: {state.teamRanks[0]} / {state.teamRanks[1]}</span>
      </div>
    );
  }
  // play phase
  // Two views: either the trick has just resolved and is on the display
  // delay (winner announcement), or someone is actively on the clock.
  const trickFull = state.currentTrickPlays.length === state.players.length;
  if (trickFull && state.completedTricks.length > 0) {
    // Display window between a complete trick and the next leader's turn.
    const last = state.completedTricks[state.completedTricks.length - 1]!;
    const winnerName = state.players[last.winner]?.displayName ?? `Seat ${last.winner}`;
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: "10px 18px" }}>
        <span className="label">Trick won by</span>
        <span className="points" style={{ fontSize: 22 }}>{winnerName}</span>
        <span className="label">+{last.points} pts</span>
      </div>
    );
  }
  if (state.currentSeat >= 0) {
    const name = state.players[state.currentSeat]?.displayName ?? `Seat ${state.currentSeat}`;
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: "10px 18px" }}>
        <span className="label">Waiting on</span>
        <span className="points" style={{ fontSize: 22 }}>{name}</span>
      </div>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// Bottom-left info widget — your hand count + trick pts collected so far
// ---------------------------------------------------------------------------

function BottomLeftInfo({
  state,
  yourSeat,
}: {
  state: ShengJiPublicState;
  yourSeat: number | null;
}) {
  // Hide entirely if there's no seated player — spectators don't have
  // a "your hand" / "your rank". Otherwise show across all phases so
  // the rank stays visible from dealing through round-end.
  if (yourSeat === null || yourSeat < 0) return null;
  const handCount = state.players[yourSeat]?.handCount ?? 0;
  const totalPoints = state.completedTricks.reduce((s, t) => s + t.points, 0);
  // Local player's team current rank. `teamRanks` is
  // `[defenders' rank, attackers' rank]`. When the player's team is
  // unknown (Find-the-Friend pre-reveal) we fall back to the
  // defenders' rank, which is the rank in play this round.
  const myTeam = state.players[yourSeat]?.team ?? null;
  const myRank =
    myTeam === null || myTeam === state.defendingTeam
      ? state.teamRanks[0]
      : state.teamRanks[1];
  return (
    <div className="board-info-bottom-left">
      <div className="board-info-line">
        <span className="label">Your rank</span>
        <span className="value">{myRank}</span>
      </div>
      <div className="board-info-line">
        <span className="label">Your hand</span>
        <span className="value">{handCount} cards</span>
      </div>
      <div className="board-info-line">
        <span className="label">Trick pts collected</span>
        <span className="value">{totalPoints}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bid lock-in countdown
// ---------------------------------------------------------------------------

/**
 * Shown in the centre of the table after the last card is dealt and the
 * engine is holding the bid open for one final window. Re-renders every
 * 100ms so the user sees seconds tick down to zero. The server resets
 * `bidLockUntil` whenever a stronger declaration lands, so this component
 * is automatically driven by the public state without any local state.
 */
function BidLockCountdown({ until }: { until: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, []);
  const remainingMs = Math.max(0, until - now);
  const seconds = Math.ceil(remainingMs / 1000);
  // Add an `urgent` class at <= 2s for a CSS pulse / colour shift.
  const urgent = remainingMs <= 2000;
  return (
    <div className={`center-pile bid-lockin ${urgent ? "urgent" : ""}`}>
      <span className="label">Bidding closes in</span>
      <span className="bid-lockin-count">{seconds}</span>
      <span className="label small">Last chance to declare or override</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase action bar (declare, pass, bury, play)
// ---------------------------------------------------------------------------

function ActionBar({
  state,
  yourSeat,
  selectedCards,
  canDeclareNow,
  isMyTurnToPlay,
  isMyTurnToBury,
  isMyTurnToCallFriends,
  send,
  onAfterSubmit,
}: {
  state: ShengJiPublicState;
  yourSeat: number | null;
  selectedCards: CardId[];
  canDeclareNow: boolean;
  isMyTurnToPlay: boolean;
  isMyTurnToBury: boolean;
  isMyTurnToCallFriends: boolean;
  send: (m: ClientMsg) => void;
  onAfterSubmit: () => void;
}) {
  if (yourSeat === null) return null;

  if (isMyTurnToCallFriends) {
    return (
      <CallFriendsBar
        state={state}
        send={send}
        onAfterSubmit={onAfterSubmit}
      />
    );
  }

  if (canDeclareNow) {
    // Player may declare during dealing. Show a button only if the current
    // selection is a valid declaration AND stronger than the current one.
    const cur = state.declaration?.strength ?? 0;
    let strength = 0;
    if (selectedCards.length === 1) {
      if (isDeclarableSingle(selectedCards[0]!, state.trumpRank)) strength = 1;
    } else if (selectedCards.length === 2) {
      const r = isDeclarablePair(selectedCards, state.trumpRank);
      if (r.ok) strength = r.strength;
    }
    const stronger = strength > cur;
    return (
      <div className="action-bar">
        <button
          className="primary"
          disabled={!stronger}
          onClick={() => {
            send({ t: "action", action: { kind: "shengji", payload: { type: "declare", cards: selectedCards } } });
            onAfterSubmit();
          }}
          title={stronger ? "Declare" : "Select a stronger declaration"}
        >
          Declare
        </button>
      </div>
    );
  }

  if (isMyTurnToBury) {
    // Kitty size varies by seat count (8 for 4–5 players, 12 for 6).
    const kittyNeed = state.kittySize;
    const ok = selectedCards.length === kittyNeed;
    return (
      <div className="action-bar">
        <button
          className="primary"
          disabled={!ok}
          onClick={() => {
            send({ t: "action", action: { kind: "shengji", payload: { type: "bury_kitty", cards: selectedCards } } });
            onAfterSubmit();
          }}
        >
          Bury {selectedCards.length}/{kittyNeed} to kitty
        </button>
      </div>
    );
  }

  if (isMyTurnToPlay) {
    const isLeading = state.currentTrickPlays.length === 0;
    const required = isLeading ? null : state.currentTrickPlays[0]!.cards.length;
    const ok = isLeading ? selectedCards.length > 0 : selectedCards.length === required;
    return (
      <div className="action-bar">
        <button
          className="primary"
          disabled={!ok}
          onClick={() => {
            send({ t: "action", action: { kind: "shengji", payload: { type: "play", cards: selectedCards } } });
            onAfterSubmit();
          }}
        >
          {isLeading ? "Lead" : `Play ${selectedCards.length}/${required} cards`}
        </button>
      </div>
    );
  }

  if (state.phase === "round_end") {
    return (
      <div className="action-bar">
        <button
          className="primary"
          onClick={() =>
            send({ t: "action", action: { kind: "shengji", payload: { type: "ready_next_round" } } })
          }
        >
          Ready for next round
        </button>
      </div>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Find-the-Friend: declarer call-cards picker
// ---------------------------------------------------------------------------

const ALL_RANKS_DESC: RankShort[] = ["A", "K", "Q", "J", "T", "9", "8", "7", "6", "5", "4", "3", "2"];
const ALL_SUITS_FOR_CALL: { id: SuitId; glyph: string; red: boolean }[] = [
  { id: "S", glyph: "♠", red: false },
  { id: "H", glyph: "♥", red: true },
  { id: "D", glyph: "♦", red: true },
  { id: "C", glyph: "♣", red: false },
];

function CallFriendsBar({
  state,
  send,
  onAfterSubmit,
}: {
  state: ShengJiPublicState;
  send: (m: ClientMsg) => void;
  onAfterSubmit: () => void;
}) {
  // Number of decks = (ordinal max). Derived from kittySize + handSize math
  // is brittle; we send `seatCount` in state instead and recover deck count
  // from the friendsToCall ordinal range — but the engine emits the deck
  // count implicitly via `kittySize`. Easier: deck count = ceil((kittySize +
  // seatCount * (cards-per-seat)) / 54). The reliable source is the public
  // state's seatCount → known mapping.
  const deckCount = state.seatCount === 6 ? 3 : 2;
  const need = state.friendsToCall;
  // One picker row per friend; each row holds a (rank, suit, ordinal, public) choice.
  // `public` defaults to true — only the *seat* who will play it stays a
  // mystery. Toggle off to also hide the called card itself.
  const [picks, setPicks] = useState<
    { rank: RankShort; suit: SuitId; ordinal: number; public: boolean }[]
  >(
    () =>
      Array.from({ length: need }, () => ({
        rank: "A" as RankShort,
        suit: "S" as SuitId,
        ordinal: 1,
        public: true,
      }))
  );
  const trumpRank = state.trumpRank;
  const ranksAvailable = ALL_RANKS_DESC.filter((r) => r !== trumpRank);
  const allValid = picks.every((p) => p.rank !== trumpRank && p.ordinal >= 1 && p.ordinal <= deckCount);
  // Reject same (rank, suit, ordinal) chosen twice.
  const dupeKeys = new Set<string>();
  let hasDup = false;
  for (const p of picks) {
    const k = `${p.rank}|${p.suit}|${p.ordinal}`;
    if (dupeKeys.has(k)) hasDup = true;
    dupeKeys.add(k);
  }
  const ok = allValid && !hasDup;
  return (
    <div className="action-bar" style={{ flexDirection: "column", alignItems: "stretch", gap: 8, maxWidth: 520 }}>
      <div className="muted" style={{ fontSize: 13, textAlign: "center" }}>
        Pick {need === 1 ? "a card" : `${need} cards`} — the first player to play each becomes your teammate.
      </div>
      {picks.map((p, i) => (
        <div key={i} className="row" style={{ gap: 6, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
          <span className="muted" style={{ fontSize: 12 }}>#{i + 1}:</span>
          <Select<RankShort>
            value={p.rank}
            onChange={(v) =>
              setPicks((prev) => prev.map((x, j) => (j === i ? { ...x, rank: v } : x)))
            }
            options={ranksAvailable.map((r) => ({ value: r, label: r }))}
            width={70}
            compact
          />
          <Select<SuitId>
            value={p.suit}
            onChange={(v) =>
              setPicks((prev) => prev.map((x, j) => (j === i ? { ...x, suit: v } : x)))
            }
            options={ALL_SUITS_FOR_CALL.map((s) => ({ value: s.id, label: s.glyph }))}
            width={70}
            compact
          />
          <Select<number>
            value={p.ordinal}
            onChange={(v) =>
              setPicks((prev) => prev.map((x, j) => (j === i ? { ...x, ordinal: v } : x)))
            }
            options={Array.from({ length: deckCount }, (_, k) => k + 1).map((n) => ({
              value: n,
              label: n === 1 ? "1st" : n === 2 ? "2nd" : `${n}th`,
            }))}
            title={`${deckCount} decks in play; pick which copy to call`}
            width={90}
            compact
          />
          <label
            className="row"
            style={{ gap: 4, fontSize: 12, cursor: "pointer", userSelect: "none" }}
            title="If on, the called card is visible to everyone (only the seat who plays it stays hidden). If off, the card itself is hidden too."
          >
            <input
              type="checkbox"
              checked={p.public}
              onChange={(e) =>
                setPicks((prev) =>
                  prev.map((x, j) => (j === i ? { ...x, public: e.target.checked } : x))
                )
              }
              style={{ width: 14, height: 14 }}
            />
            <span>Public</span>
          </label>
        </div>
      ))}
      {hasDup && (
        <div className="muted" style={{ fontSize: 12, color: "var(--accent)", textAlign: "center" }}>
          Two calls can't target the same card.
        </div>
      )}
      <button
        className="primary"
        disabled={!ok}
        onClick={() => {
          send({
            t: "action",
            action: { kind: "shengji", payload: { type: "call_friends", calls: picks } },
          });
          onAfterSubmit();
        }}
      >
        Call {need === 1 ? "friend" : "friends"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Friend-calls panel — shows pending / revealed calls (top-right corner)
// ---------------------------------------------------------------------------

function FriendCallsPanel({
  state,
  yourSeat,
  yourFriendCalls,
}: {
  state: ShengJiPublicState;
  yourSeat: number | null;
  yourFriendCalls: ShengJiGameProps["yourFriendCalls"];
}) {
  // Collapsible: defaults to shown but the user can hide it to free up
  // the right side of the table.
  const [open, setOpen] = useState(true);
  if (state.friendMode !== "find_the_friend") return null;
  if (state.phase === "dealing" || state.phase === "kitty") return null;
  if (state.phase === "call_friends" && state.declarer !== yourSeat) return null;
  // Build a uniform display list. The declarer always sees the full card
  // (via the private channel); everyone else sees what the public snapshot
  // exposes — full details for public calls, "hidden" for private ones until
  // played.
  type DisplayCall = {
    rank: RankShort | "?";
    suit: SuitId | "?";
    ordinal: number;
    revealedSeat: number | null;
  };
  const calls: DisplayCall[] = yourFriendCalls
    ? yourFriendCalls.map((c) => ({
        rank: c.rank,
        suit: c.suit,
        ordinal: c.ordinal,
        revealedSeat: c.revealedSeat,
      }))
    : (state.friendCalls ?? []).map((c) =>
        c.hidden
          ? { rank: "?", suit: "?", ordinal: 0, revealedSeat: null }
          : {
              rank: c.rank,
              suit: c.suit,
              ordinal: c.ordinal,
              revealedSeat: c.revealedSeat,
            }
      );
  if (calls.length === 0) return null;
  if (!open) {
    return (
      <button
        className="friend-calls-collapsed"
        onClick={() => setOpen(true)}
        title="Show friend calls"
        aria-label="Show friend calls"
      >
        Friend calls ({calls.length})
      </button>
    );
  }
  return (
    <div className="friend-calls-panel">
      <button
        className="friend-calls-collapse-btn"
        onClick={() => setOpen(false)}
        title="Hide friend calls"
        aria-label="Hide friend calls"
      >
        ×
      </button>
      <div className="friend-calls-title">Friend calls</div>
      {calls.map((c, i) => {
        const seatName =
          c.revealedSeat !== null
            ? state.players[c.revealedSeat]?.displayName ?? `Seat ${c.revealedSeat}`
            : null;
        const isHidden = c.rank === "?";
        const suitGlyph =
          c.suit === "S" ? "♠" : c.suit === "H" ? "♥" : c.suit === "D" ? "♦" : c.suit === "C" ? "♣" : "?";
        const isRed = c.suit === "H" || c.suit === "D";
        return (
          <div key={i} className={`friend-call-row ${c.revealedSeat !== null ? "revealed" : ""}`}>
            <span className="friend-call-ord">#{i + 1}</span>
            <span className="friend-call-card">
              {isHidden ? (
                <em className="muted">hidden</em>
              ) : (
                <>
                  <span className={isRed ? "red-suit" : ""}>{c.rank}{suitGlyph}</span>
                  <span className="muted" style={{ marginLeft: 4, fontSize: 11 }}>
                    ({c.ordinal === 1 ? "1st" : c.ordinal === 2 ? "2nd" : `${c.ordinal}th`})
                  </span>
                </>
              )}
            </span>
            <span className="friend-call-seat">
              {seatName ? (
                <span className="role-tag partner">{seatName}</span>
              ) : (
                <span className="muted" style={{ fontSize: 11 }}>pending</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Event log
// ---------------------------------------------------------------------------

export function EventLog({ events, state }: { events: GameEvent[]; state: ShengJiPublicState }) {
  const lines: string[] = [];
  for (const e of events.slice(-12)) {
    if (e.text) {
      lines.push(e.text);
    } else if (e.type === "deal_card") {
      // skip; too noisy
    } else if (e.type === "lead" || e.type === "follow") {
      // skip; visible on the table
    } else if (e.type === "trick_complete") {
      const data = e.data as { winner?: number; points?: number } | undefined;
      if (data) {
        const name = data.winner !== undefined ? state.players[data.winner]?.displayName : "?";
        lines.push(`Trick → ${name} (+${data.points} pts)`);
      }
    } else {
      lines.push(e.type);
    }
  }
  if (lines.length === 0) return null;
  return (
    <div className="event-log">
      {lines.map((l, i) => <div className="line" key={i}>{l}</div>)}
    </div>
  );
}
